import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { IMG } from "@/lib/data";
import { RevealLines, RevealWords } from "@/components/RevealText";
import { Reveal, StaggerContainer, StaggerItem } from "@/components/Reveal";
import EditorialMarquee from "@/components/EditorialMarquee";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";

function AboutHero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  return (
    <section
      ref={ref}
      className="relative min-h-[92svh] w-full overflow-hidden bg-obsidian text-bone"
    >
      <motion.div style={{ y }} className="absolute inset-0">
        <img
          src={IMG.quinnsAtWork}
          alt="John and Jennifer Quinn working the herd with a Charolais cow"
          className="h-[115%] w-full object-cover"
          loading="eager"
        />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-b from-obsidian/70 via-obsidian/20 to-obsidian/5" />
      <div className="absolute inset-0 noise-overlay opacity-60" />

      <div className="relative z-10 mx-auto max-w-[1600px] px-6 md:px-10 lg:px-14 pt-40 md:pt-44">
        <RevealWords
          text="— About · The Quinns"
          className="font-mono text-[11px] tracking-overline uppercase text-bone/85"
          delay={0.2}
        />
        <RevealLines
          as="h1"
          lines={["Rooted in the", "Bitterroot Valley."]}
          className="mt-8 font-serif text-[13vw] md:text-[8.5vw] lg:text-[7.2vw] leading-[0.92] tracking-tightest text-bone max-w-[18ch]"
          delay={0.35}
          stagger={0.13}
        />
      </div>
    </section>
  );
}

function StorySection() {
  return (
    <section className="relative bg-bone text-ink noise-overlay-light">
      <div className="relative mx-auto max-w-[1600px] px-6 md:px-10 lg:px-14 py-28 md:py-40">
        <div className="grid grid-cols-12 gap-10 items-start">
          <Reveal className="col-span-12 md:col-span-5">
            <p className="font-mono text-[11px] tracking-overline uppercase text-terracotta">
              The Story
            </p>
            <h2 className="mt-4 font-serif text-4xl md:text-5xl leading-[1] tracking-tight text-ink">
              John & Jennifer Quinn.
            </h2>
            <div className="mt-8 clip-editorial-tall relative overflow-hidden max-w-[420px]">
              <img
                src={IMG.johnAndJennifer}
                alt="John and Jennifer Quinn on the ranch"
                className="h-full w-full object-cover"
                loading="lazy"
              />
              <div className="absolute inset-0 pointer-events-none noise-overlay-light" />
            </div>
            <p className="mt-3 font-mono text-[10px] tracking-overline uppercase text-sage">
              John & Jennifer Quinn — Hideaway Cattle Co.
            </p>
          </Reveal>

          <Reveal className="col-span-12 md:col-span-6 md:col-start-7 md:pt-8" y={30}>
            <p className="text-ink/85 leading-relaxed text-lg font-light max-w-[64ch]">
              Hideaway Cattle Company is a small, focused seedstock program
              raising Registered Black Angus and Charolais in Montana&apos;s
              Bitterroot Valley. We breed for the whole animal — phenotype first.
              Moderate, balanced, structurally correct cattle that are right from
              the ground up, never chasing a single extreme.
            </p>
            <p className="mt-6 text-ink/80 leading-relaxed max-w-[64ch]">
              We select hardest for the traits that keep a cow in the herd:
              sound feet and legs, tight well-attached udders, fertility and
              honest maternal ability. A good female breeds back on time, raises
              a growthy calf on her own milk, and does it year after year on
              grass. We cull hard, keep only what works, and stand behind every
              animal that leaves the ranch.
            </p>
            <p className="mt-8 text-ink/70 italic font-serif text-2xl md:text-3xl max-w-[42ch] leading-[1.15]">
              &ldquo;A cow has to earn her way every year — with her feet, her
              udder, and the calf at her side.&rdquo;
            </p>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

function ImageBand() {
  return (
    <section className="relative bg-bone">
      <div className="mx-auto max-w-[1600px] px-6 md:px-10 lg:px-14 pb-24">
        <div className="grid grid-cols-12 gap-6">
          <Reveal className="col-span-12 md:col-span-7">
            <div className="clip-editorial-tall md:aspect-[4/5] relative overflow-hidden">
              <img
                src={IMG.johnShowRing}
                alt="John Quinn mentoring a young exhibitor in the show ring"
                className="h-full w-full object-cover"
                loading="lazy"
              />
            </div>
            <p className="mt-3 font-mono text-[10px] tracking-overline uppercase text-sage">
              In the ring, giving back to the next generation.
            </p>
          </Reveal>
          <Reveal className="col-span-12 md:col-span-5 md:pt-16" y={40}>
            <div className="clip-editorial-tall relative overflow-hidden">
              <img
                src={IMG.wheelLinePeaks}
                alt="Wheel-line irrigation and snow-capped Bitterroot peaks over green pasture"
                className="h-full w-full object-cover"
                loading="lazy"
              />
            </div>
            <p className="mt-3 font-mono text-[10px] tracking-overline uppercase text-sage">
              Irrigated ground beneath the Bitterroots.
            </p>
            <div className="mt-6 clip-cinematic relative overflow-hidden">
              <img
                src={IMG.calfInGrass}
                alt="Black Angus calf bedded in tall spring grass"
                className="h-full w-full object-cover"
                loading="lazy"
              />
            </div>
            <p className="mt-3 font-mono text-[10px] tracking-overline uppercase text-sage">
              A calf bedded down in the tall grass.
            </p>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

function ValuesGrid() {
  const values = [
    [
      "Phenotype",
      "Balanced, moderate, structurally correct cattle — right from the ground up, never chasing a single extreme.",
    ],
    [
      "Feet & Legs",
      "Sound feet and correct leg structure are longevity. We keep cattle that travel well and stay in the herd for years.",
    ],
    [
      "Udder Quality",
      "Tight, well-attached udders with sound teats. A good udder raises a good calf, unassisted, every spring.",
    ],
    [
      "Maternal Traits",
      "Fertility, milk and honest mothering. Females that breed back on time and wean a calf year after year.",
    ],
  ];
  return (
    <section className="relative bg-parchment text-ink">
      <div className="mx-auto max-w-[1600px] px-6 md:px-10 lg:px-14 py-24 md:py-32">
        <Reveal>
          <p className="font-mono text-[11px] tracking-overline uppercase text-sage">
            — Standards
          </p>
          <h3 className="mt-4 font-serif text-4xl md:text-5xl tracking-tight max-w-3xl">
            Four things we won&apos;t compromise on.
          </h3>
        </Reveal>
        <StaggerContainer className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-y-10 gap-x-16">
          {values.map(([t, s]) => (
            <StaggerItem
              key={t}
              className="border-t border-border_subtle pt-6 flex items-start gap-6"
            >
              <span
                aria-hidden
                className="font-mono text-terracotta pt-1 min-w-[24px]"
              >
                —
              </span>
              <div>
                <h4 className="font-serif text-3xl text-ink tracking-tight">
                  {t}
                </h4>
                <p className="mt-2 text-ink/70 leading-relaxed max-w-[46ch]">
                  {s}
                </p>
              </div>
            </StaggerItem>
          ))}
        </StaggerContainer>
      </div>
    </section>
  );
}

function AboutClose() {
  return (
    <section className="relative bg-bone">
      <div className="mx-auto max-w-[1600px] px-6 md:px-10 lg:px-14 py-24 md:py-32">
        <Reveal>
          <p className="font-mono text-[11px] tracking-overline uppercase text-sage">
            — Visit
          </p>
          <h3 className="mt-4 font-serif text-5xl md:text-6xl tracking-tight max-w-3xl leading-[1]">
            Come see the cattle in person.
          </h3>
          <Link
            to="/contact"
            className="mt-10 inline-flex items-center gap-3 px-6 py-3.5 bg-ink text-bone text-[12px] uppercase tracking-overline hover:bg-terracotta transition-colors"
            data-testid="about-contact-cta"
          >
            Schedule a visit
            <ArrowRight className="h-4 w-4" />
          </Link>
        </Reveal>
      </div>
    </section>
  );
}

export default function About() {
  return (
    <div className="bg-bone">
      <AboutHero />
      <EditorialMarquee
        phrases={[
          "Stevensville, Montana",
          "Bitterroot Valley",
          "John & Jennifer Quinn",
          "A quiet standard",
        ]}
      />
      <StorySection />
      <ImageBand />
      <ValuesGrid />
      <AboutClose />
    </div>
  );
}
